import axios from 'axios';

// Remove barra final se existir
const normalizeURL = (url) => url?.replace(/\/$/, '');

const API_BASE_URL = normalizeURL(process.env.REACT_APP_API_URL);

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Interceptor para adicionar token
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  // Se o body é FormData, deixar o axios/browser definir o Content-Type com boundary
  if (config.data instanceof FormData) {
    delete config.headers['Content-Type'];
  }
  return config;
});

// Interceptor para tratar erros e renovar token
api.interceptors.response.use(
  (response) => {
    const newToken = response.headers['x-new-token'];
    if (newToken) localStorage.setItem('token', newToken);
    return response;
  },
  (error) => {
    if (error.response?.status === 401) {
      // Não redirecionar se o erro for na própria rota de login
      const isLoginRequest = error.config?.url?.includes('/auth/login');
      
      if (!isLoginRequest) {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        
        // Disparar evento para sincronizar com outras abas e atualizar AuthContext
        window.dispatchEvent(new StorageEvent('storage', {
          key: 'token',
          oldValue: 'removed',
          newValue: null,
          url: window.location.href
        }));
        
        window.location.href = '/login';
      }
    }
    if (error.response?.status === 403) {
      console.warn('Acesso negado:', error.response?.data?.message);
    }
    return Promise.reject(error);
  }
);

export const authAPI = {
  login: (credentials) => api.post('/auth/login', credentials),
  register: (data) => api.post('/auth/register', data),
  check: () => api.get('/auth/check'),
};

export const instancesAPI = {
  list: (clienteId) => api.get(`/instancias-whatsapp?cliente_id=${clienteId}`),
  getUsage: (instanceId) => api.get(`/instancias-whatsapp/${instanceId}/usage`),
  getBusinessProfile: (instanceId) => api.get(`/instancias-whatsapp/${instanceId}/business-profile`),
  updateBusinessProfile: (instanceId, data) => api.patch(`/instancias-whatsapp/${instanceId}/business-profile`, data),
  updateDisplayName: (instanceId, displayName) => api.patch(`/instancias-whatsapp/${instanceId}/display-name`, { display_name: displayName }),
  uploadProfilePhoto: (instanceId, formData) => api.post(`/instancias-whatsapp/${instanceId}/profile-photo`, formData),
  create: (data) => api.post('/instancias-whatsapp/criar', data),
  getStatus: (clienteId) => api.get(`/instancias-whatsapp/status/${clienteId}`),
  getStatusReal: (clienteId) => api.get(`/instancias-whatsapp/status/${clienteId}`),
  activateIA: (clienteId) => api.post(`/instancias-whatsapp/ativar-ia-cliente/${clienteId}`),
  deactivateIA: (clienteId) => api.post(`/instancias-whatsapp/desativar-ia-cliente/${clienteId}`),
  updateConfiguracoes: (clienteId, data) => api.post(`/instancias-whatsapp/atualizar-configuracoes/${clienteId}`, data),
  activate: (clienteId) => api.post(`/instancias-whatsapp/ativar/${clienteId}`),
  deactivate: (clienteId) => api.post(`/instancias-whatsapp/desativar/${clienteId}`),
  clearSession: (clienteId) => api.post(`/instancias-whatsapp/limpar-sessao/${clienteId}`),
  listActiveSessions: () => api.get('/instancias-whatsapp/sessoes-ativas'),
  removeSession: (sessionId) => api.post(`/instancias-whatsapp/remover-sessao/${sessionId}`),
  clearAllSessions: () => api.post('/instancias-whatsapp/limpar-todas-sessoes'),
  reconnectInstance: (clienteId) => api.post(`/instancias-whatsapp/reconectar/${clienteId}`),
  initializeClient: (clienteId) => api.post(`/instancias-whatsapp/inicializar-cliente/${clienteId}`),
  getBrowserStatus: () => api.get('/instancias-whatsapp/verificar-browser'),
  getListenerStatus: (clienteId) => api.get(`/instancias-whatsapp/verificar-listener/${clienteId}`),
  
  // Meta API endpoints
  configureMetaApi: (clienteId, data) => api.post(`/instancias-whatsapp/configurar-meta-api/${clienteId}`, data),
  getMetaApiConfig: (clienteId) => api.get(`/instancias-whatsapp/meta-api-config/${clienteId}`),
  verifyConnection: (instanceId) => api.get(`/instancias-whatsapp/${instanceId}/verificar-conexao`),
  uploadCertificate: (instanceId, publicKey) => api.post(`/instancias-whatsapp/${instanceId}/upload-certificado`, { public_key: publicKey }),
  verifyCertificate: (instanceId) => api.get(`/instancias-whatsapp/${instanceId}/verificar-certificado`),
  
  // Gerenciamento de múltiplas instâncias
  setDefault: (instanceId, clienteId) => api.post(`/instancias-whatsapp/${instanceId}/definir-padrao?cliente_id=${clienteId}`),
  delete: (instanceId, clienteId) => api.delete(`/instancias-whatsapp/${instanceId}?cliente_id=${clienteId}`),
  configureMetaApiById: (instanceId, data) => api.post(`/instancias-whatsapp/${instanceId}/configurar-meta-api`, data),
  configurarZApi: (instanceId, data) => api.post(`/instancias-whatsapp/${instanceId}/configurar-z-api`, data),
  getZApiQrCode: (instanceId) => api.get(`/instancias-whatsapp/${instanceId}/z-api-qrcode`),
  disconnectZApi: (instanceId) => api.post(`/instancias-whatsapp/${instanceId}/z-api-disconnect`),
  setFluxo: (instanceId, fluxoId) => api.patch(`/instancias-whatsapp/${instanceId}/definir-fluxo`, { fluxo_id: fluxoId }),
  updateConfiguracoes: (instanceId, data) => api.patch(`/instancias-whatsapp/${instanceId}/configuracoes`, data),
  
  // Outros endpoints
  diagnostico: (clienteId) => api.get(`/instancias-whatsapp/diagnostico/${clienteId}`),
  corrigirAutomatico: (clienteId) => api.post(`/instancias-whatsapp/corrigir-automatico/${clienteId}`),
  toggleFeature: (clienteId, feature, action) => api.post(`/instancias-whatsapp/toggle-feature/${clienteId}`, { feature, action }),
  reconectar: (clienteId) => api.post(`/instancias-whatsapp/reconectar/${clienteId}`),
  iniciarListener: (clienteId) => api.post(`/instancias-whatsapp/iniciar-listener/${clienteId}`),
  limparListeners: (clienteId) => api.post(`/instancias-whatsapp/limpar-listeners/${clienteId}`),
  reiniciarListener: (clienteId) => api.post(`/instancias-whatsapp/reiniciar-listener/${clienteId}`),
  verificarListener: (clienteId) => api.get(`/instancias-whatsapp/verificar-listener/${clienteId}`),
  verificarBrowser: () => api.get('/instancias-whatsapp/verificar-browser'),
  restart: (clienteId) => api.post(`/instancias-whatsapp/reiniciar/${clienteId}`),
  resetInstance: (clienteId) => api.post(`/instancias-whatsapp/resetar-instancia/${clienteId}`),
  connect: (clienteId) => api.post(`/instancias-whatsapp/conectar/${clienteId}`),
  disconnect: (clienteId) => api.post(`/instancias-whatsapp/desconectar/${clienteId}`),
  disconnectAPI: (clienteId) => api.post(`/instancias-whatsapp/desconectar/${clienteId}`),
  reiniciarBrowser: () => api.post('/instancias-whatsapp/reiniciar-browser'),
  getStatusFront: (clienteId) => api.get(`/instancias-whatsapp/status-front/${clienteId}`),
  limparTodasSessoes: () => api.post('/instancias-whatsapp/limpar-todas-sessoes'),
  getAccountInfo: (instanceId) => api.get(`/instancias-whatsapp/${instanceId}/account-info`),
};

export const contactsAPI = {
  list: (clienteId, page = 1, limit = 50, search = '', status = '', temperature = '') => api.get(`/contatos/list/${clienteId}?page=${page}&limit=${limit}${search ? `&search=${encodeURIComponent(search)}` : ''}${status ? `&status=${status}` : ''}${temperature ? `&temperature=${temperature}` : ''}`),
  autocomplete: (clienteId, query) => api.get(`/contatos/autocomplete/${clienteId}?q=${encodeURIComponent(query)}`),
  search: (clienteId) => api.get(`/contatos/list/${clienteId}?limit=10000`),
  create: (data) => api.post('/contatos', data),
  update: (clienteId, id, data) => api.patch(`/contatos/${clienteId}/${id}`, data),
  delete: (clienteId, id) => api.delete(`/contatos/${clienteId}/${id}`),
  get: (clienteId, id) => api.get(`/contatos/${clienteId}/${id}`),
  getOne: (clienteId, id) => api.get(`/contatos/${clienteId}/${id}`),
  importCSV: (data) => api.post('/contatos/import-csv', data),
  addCanal: (id, data) => api.post(`/contatos/${id}/canais`, data),
  removeCanal: (canalId) => api.delete(`/contatos/canais/${canalId}`),
  getByTelefone: (clienteId, telefone) => api.get(`/contatos/por-telefone/${clienteId}/${telefone}`),
  addComentario: (id, data) => api.post(`/contatos/${id}/comentarios`, data),
  getComentarios: (id) => api.get(`/contatos/${id}/comentarios`),
  toggleOptIn: (id, optIn) => api.patch(`/contatos/${id}/opt-in`, { opt_in_ativo: optIn }),
};

export const groupsAPI = {
  list: (clienteId) => api.get(`/grupos${clienteId ? `?cliente_id=${clienteId}` : ''}`),
  get: (id) => api.get(`/grupos/${id}`),
  create: (data) => api.post('/grupos', data),
  update: (id, data) => api.patch(`/grupos/${id}`, data),
  delete: (id) => api.delete(`/grupos/${id}`),
  addContact: (data) => api.post('/grupos/contato', Array.isArray(data) ? data : [data]),
  removeContact: (id) => api.delete(`/grupos/contato/${id}`),
  getContacts: (groupId) => api.get(`/grupos/${groupId}/contatos`),
  importCSV: (groupId, data) => api.post(`/grupos/${groupId}/import-csv`, data),
};

export const waGroupsAPI = {
  list: (clienteId, params) => api.get('/grupos-whatsapp', { params: { cliente_id: clienteId, ...params } }),
  get: (id, clienteId) => api.get(`/grupos-whatsapp/${id}`, { params: { cliente_id: clienteId } }),
  resumo: (clienteId) => api.get('/grupos-whatsapp/resumo', { params: { cliente_id: clienteId } }),
  exportCompleto: (clienteId, agruparPor) => api.get(`/grupos-whatsapp/export-completo/${clienteId}`, { params: { agrupar_por: agruparPor }, responseType: 'blob' }),
  sync: (instanceId, clienteId) => api.post('/grupos-whatsapp/sync', { instancia_id: instanceId, cliente_id: clienteId }),
  create: (data) => api.post('/grupos-whatsapp/create', data),
  sendMessage: (data) => api.post('/grupos-whatsapp/send-message', data),
  getEngagement: (id, clienteId, params) => api.get(`/grupos-whatsapp/${id}/engagement`, { params: { cliente_id: clienteId, ...params } }),
  getTemperature: (id, clienteId, params) => api.get(`/grupos-whatsapp/${id}/temperature`, { params: { cliente_id: clienteId, ...params } }),
  getInsights: (id, clienteId) => api.get(`/grupos-whatsapp/${id}/insights`, { params: { cliente_id: clienteId } }),
  getWordCloud: (id, clienteId, params) => api.get(`/grupos-whatsapp/${id}/wordcloud`, { params: { cliente_id: clienteId, ...params } }),
  getHeatmap: (clienteId, params) => api.get('/grupos-whatsapp/heatmap/data', { params: { cliente_id: clienteId, ...params } }),
  getAnalytics: (clienteId, params) => api.get('/conversas/grupo/analytics', { params: { cliente_id: clienteId, ...params } }),
  // Categorias
  getCategorias: () => api.get('/grupo-categorias'),
  createCategoria: (data) => api.post('/grupo-categorias', data),
  updateCategoria: (id, data) => api.put(`/grupo-categorias/${id}`, data),
  deleteCategoria: (id) => api.delete(`/grupo-categorias/${id}`),
  createOpcao: (categoriaId, data) => api.post(`/grupo-categorias/${categoriaId}/opcoes`, data),
  updateOpcao: (id, data) => api.put(`/grupo-categorias/opcoes/${id}`, data),
  deleteOpcao: (id) => api.delete(`/grupo-categorias/opcoes/${id}`),
  // Atribuição de categorias ao grupo
  setGrupoCategorias: (grupoId, valores) => api.put(`/grupos-whatsapp/${grupoId}/categorias`, { valores }),
  getGrupoCategorias: (grupoId) => api.get(`/grupos-whatsapp/${grupoId}/categorias`),
  // Responsável e meta
  setResponsavel: (grupoId, contatoId) => api.put(`/grupos-whatsapp/${grupoId}/responsavel`, { contato_id: contatoId }),
  setMeta: (grupoId, meta) => api.put(`/grupos-whatsapp/${grupoId}/meta`, { meta_participantes: meta }),
  getMetaGeral: (clienteId) => api.get(`/grupos-whatsapp/meta-geral/${clienteId}`),
  // Histórico datado de participantes
  getEventosPorGrupo: (clienteId, params) => api.get(`/grupos-whatsapp/eventos/por-grupo/${clienteId}`, { params }),
  getContatosUnicosSerie: (clienteId, params) => api.get(`/grupos-whatsapp/contatos-unicos/serie/${clienteId}`, { params }),
};

export const campaignsAPI = {
  list: (params) => api.get('/campanhas', { params }),
  get: (id) => api.get(`/campanhas/${id}`),
  create: (data) => api.post('/campanhas', data),
  update: (id, data) => api.patch(`/campanhas/${id}`, data),
  delete: (id) => api.delete(`/campanhas/${id}`),
  execute: (id) => api.post(`/campanhas/${id}/execute`),
  pausar: (id) => api.put(`/campanhas/${id}/pausar`),
  cancelar: (id) => api.put(`/campanhas/${id}/cancelar`),
  retomar: (id) => api.put(`/campanhas/${id}/retomar`),
  duplicar: (id) => api.post(`/campanhas/${id}/duplicar`),
  getMessages: (id) => api.get(`/campanhas/${id}/messages`),
  getMediaUrl: (mediaId) => api.get(`/campanhas/media-url/${mediaId}`),
  addMessage: (id, data) => api.post(`/campanhas/${id}/messages`, data),
  updateMessage: (id, messageId, data) => api.patch(`/campanhas/${id}/messages/${messageId}`, data),
  removeMessage: (id, messageId) => api.delete(`/campanhas/${id}/messages/${messageId}`),
  getStats: (id) => api.get(`/campanhas/${id}/stats`),
  getRecipients: (id, params) => api.get(`/campanhas/${id}/recipients`, { params }),
  exportRecipients: (id, params) => api.get(`/campanhas/${id}/recipients/export`, { params, responseType: 'blob' }),
  clearHistory: (id) => api.delete(`/campanhas/${id}/history`),
  getPendentes: (id) => api.get(`/campanhas/${id}/contatos-pendentes`),
  getFila: (id) => api.get(`/campanhas/${id}/fila`),
  reenfileirarSuspeitos: (id, ids) =>
    api.post(`/campanhas/${id}/fila/reenfileirar-suspeitos`, ids ? { ids } : {}),
  getCusto: (id) => api.get(`/campanhas/${id}/custo`),
  reenviarPendentes: (id) => api.post(`/campanhas/${id}/reenviar-pendentes`),
  submeterTemplate: (id, data) => api.post(`/campanhas/${id}/submeter-template`, data),
  verificarStatusTemplate: (id) => api.get(`/campanhas/${id}/status-template`),
  uploadMedia: (usuarioId, formData) => api.post(`/campanhas/upload-media/${usuarioId}`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
};

export const personasAPI = {
  // Personas de disparo: uma identidade que fala por vários números.
  list: () => api.get('/personas'),
  get: (id) => api.get(`/personas/${id}`),
  create: (data) => api.post('/personas', data),
  update: (id, data) => api.patch(`/personas/${id}`, data),
  deactivate: (id) => api.delete(`/personas/${id}`),
  availableInstances: () => api.get('/personas/instancias-disponiveis'),
  linkInstance: (id, instancia_id, rotulo_interno) =>
    api.post(`/personas/${id}/instancias`, { instancia_id, rotulo_interno }),
  unlinkInstance: (instanciaId) => api.delete(`/personas/instancias/${instanciaId}`),
  syncTemplates: (id, instanciaId) =>
    api.post(`/personas/${id}/instancias/${instanciaId}/sincronizar-templates`),
};

export const messagesAPI = {
  send: (data) => api.post('/mensagens', data),
  schedule: (data) => api.post('/mensagens/schedule', data),
  getHistory: (usuarioId) => api.get(`/mensagens/register/${usuarioId}`),
  getByCliente: () => api.get('/mensagens/cliente'),
  getByGroup: (groupId) => api.get(`/grupos/${groupId}`),
  update: (id, data) => api.patch(`/mensagens/${id}`, data),
  getVariables: () => api.get('/mensagens/variaveis'),
  delete: (id) => api.delete(`/mensagens/${id}`),
};

export const autoRulesAPI = {
  list: (clienteId) => api.get(`/regras-automaticas/cliente/${clienteId}`),
  create: (data) => api.post('/regras-automaticas', data),
  update: (id, data) => api.patch(`/regras-automaticas/${id}`, data),
  delete: (id) => api.delete(`/regras-automaticas/${id}`),
};

export const iaAPI = {
  getConfig: (clienteId) => api.get(`/ia-assistant/config/cliente/${clienteId}`),
  listConfigs: (clienteId) => api.get(`/ia-assistant/list/cliente/${clienteId}`),
  createConfig: (data) => api.post('/ia-assistant', data),
  updateConfig: (clienteId, data) => api.patch(`/ia-assistant/${clienteId}`, data),
  testResponse: (clienteId, data) => api.post(`/ia-assistant/test/${clienteId}`, data),
  deleteConfig: (id) => api.delete(`/ia-assistant/${id}`),
};

export const fluxosAPI = {
  // Sem page/limit o backend devolve tudo — os selects de fluxo em campanhas,
  // instancias e publicacoes dependem da lista completa.
  list: (clienteId, params = {}) => {
    const qs = new URLSearchParams();
    if (params.page) qs.set('page', params.page);
    if (params.limit) qs.set('limit', params.limit);
    if (params.search) qs.set('search', params.search);
    if (params.ativo !== undefined) qs.set('ativo', params.ativo);
    const cauda = qs.toString() ? `?${qs}` : '';
    return api.get(`/fluxos-conversa/cliente/${clienteId}${cauda}`);
  },
  create: (clienteId, data) => api.post(`/fluxos-conversa/cliente/${clienteId}`, data),
  get: (id) => api.get(`/fluxos-conversa/${id}`),
  update: (id, data) => api.put(`/fluxos-conversa/${id}`, data),
  delete: (id) => api.delete(`/fluxos-conversa/${id}`),
  duplicar: (id) => api.post(`/fluxos-conversa/${id}/duplicar`),
  getVariaveis: (id) => api.get(`/fluxos-conversa/${id}/variaveis`),
  setVariaveis: (id, variaveis) => api.put(`/fluxos-conversa/${id}/variaveis`, { variaveis }),
  getValores: (id, escopoTipo, escopoId) =>
    api.get(`/fluxos-conversa/${id}/valores/${escopoTipo}/${escopoId}`),
  setValores: (id, escopoTipo, escopoId, valores) =>
    api.put(`/fluxos-conversa/${id}/valores/${escopoTipo}/${escopoId}`, { valores }),
};

export const tagsAPI = {
  list: (clienteId) => api.get(`/tags-atendimento?cliente_id=${clienteId}`),
  create: (data) => api.post('/tags-atendimento', data),
  update: (id, data) => api.patch(`/tags-atendimento/${id}`, data),
  getConversas: (tagId) => api.get(`/tags-atendimento/conversas?tag_id=${tagId}`),
};

export const templatesMetaAPI = {
  list: () => api.get('/templates-meta'),
  sync: (instanciaId) => api.post(`/templates-meta/sync/${instanciaId}`),
  get: (id) => api.get(`/templates-meta/${id}`),
  create: (data) => api.post('/templates-meta', data),
  update: (id, data) => api.patch(`/templates-meta/${id}`, data),
  delete: (id) => api.delete(`/templates-meta/${id}`),
  submeter: (id, instanciaIds) => api.post(`/templates-meta/${id}/submeter`, instanciaIds ? { instancia_ids: instanciaIds } : {}),
  submeterMulti: (id, instanciaIds) => api.post(`/templates-meta/${id}/submeter-multi`, { instancia_ids: instanciaIds }),
  verificarStatus: (id) => api.get(`/templates-meta/${id}/verificar-status`),
  getInstancias: (id) => api.get(`/templates-meta/${id}/instancias`),
  adicionarInstancia: (id, instanciaId) => api.post(`/templates-meta/${id}/adicionar-instancia`, { instancia_id: instanciaId }),
  copiarTemplates: (instanciaOrigemId, instanciaDestinoIds, templateIds) => api.post('/templates-meta/copiar-templates', { instancia_origem_id: instanciaOrigemId, instancia_destino_ids: instanciaDestinoIds, template_ids: templateIds }),
};

export const sessionsAPI = {
  pauseIA: (data) => api.post('/sessoes-atendimento/pausar-ia', data),
  reactivateIA: (data) => api.post('/sessoes-atendimento/reativar-ia', data),
  getStatus: (clienteId, usuarioId, telefone) => 
    api.get(`/sessoes-atendimento/status/${clienteId}/${usuarioId}/${telefone}`),
  getConfig: (clienteId) => api.get(`/sessoes-atendimento/config/${clienteId}`),
  updateConfig: (clienteId, data) => api.patch(`/sessoes-atendimento/config/${clienteId}`, data),
  markHuman: (data) => api.post('/sessoes-atendimento/marcar-humano', data),
};

// Novos endpoints para monitoramento e controle avançado
export const monitoringAPI = {
  getSystemHealth: () => api.get('/monitoring/health'),
  getBrowserStatus: () => instancesAPI.verificarBrowser(),
  getInstanceDiagnostic: (clienteId) => instancesAPI.diagnostico(clienteId),
  autoFix: (clienteId) => instancesAPI.corrigirAutomatico(clienteId),
};

export const atendimentoAPI = {
  getConversasPendentes: (clienteId) => api.get(`/atendimento/conversas/${clienteId}`),
  pausarIA: (data) => api.post('/atendimento/pausar-ia', data),
  reativarIA: (data) => api.post('/atendimento/reativar-ia', data),
  verificarSessao: (telefone, clienteId) => api.get(`/atendimento/sessao/${telefone}/${clienteId}`),
};

export const usuariosAPI = {
  list: () => api.get('/usuarios'),
  listMinhaConta: () => api.get('/usuarios/minha-conta'),
  get: (id) => api.get(`/usuarios/${id}`),
  create: (data) => api.post('/usuarios', data),
  update: (id, data) => api.patch(`/usuarios/${id}`, data),
  delete: (id) => api.delete(`/usuarios/${id}`),
  alterarNivelAcesso: (data) => api.patch('/usuarios/alterar-nivel-acesso', data),
};

export const niveisAcessoAPI = {
  getTodos: () => api.get('/nivel-acesso/todos'),
  getDescricoes: () => api.get('/nivel-acesso/descricoes'),
};

export const clientesAPI = {
  list: () => api.get('/clientes'),
  get: (id) => api.get(`/clientes/${id}`),
  create: (data) => api.post('/clientes', data),
  update: (data) => api.patch('/clientes', data),
  delete: (id) => api.delete(`/clientes/${id}`),
};

export const mediaAPI = {
  getSignedUrl: (s3Url) => api.get('/media/signed-url', { params: { url: s3Url } }),
  upload: (clienteId, usuarioId, formData) => api.post(`/media/cliente/${clienteId}/${usuarioId}`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  list: (clienteId) => api.get(`/media/list/${clienteId}`),
};

export const zApiAdminAPI = {
  listar: () => api.get('/admin/z-api/instancias'),
  listarDisponiveis: () => api.get('/admin/z-api/instancias/disponiveis'),
  criar: (data) => api.post('/admin/z-api/instancias', data),
  assinar: (id) => api.post(`/admin/z-api/instancias/${id}/assinar`),
};

// Somente nível admin. Provider, modelo e chave de IA por cliente.
export const iaAdminAPI = {
  catalogo: () => api.get('/admin/ia/modelos'),
  listarConfigs: (clienteId) => api.get(`/admin/ia/clientes/${clienteId}/configs`),
  obterConfig: (id) => api.get(`/admin/ia/configs/${id}`),
  atualizarConfig: (id, data) => api.patch(`/admin/ia/configs/${id}`, data),
  testar: (id, mensagem) => api.post(`/admin/ia/configs/${id}/testar`, { mensagem }),
  migrarChaves: () => api.post('/admin/ia/migrar-chaves'),
};

// Utilitários para facilitar o uso
export const whatsappUtils = {
  // Verificar se instância está completamente funcional
  isFullyOperational: (instance) => {
    return instance?.status === 'conectada' && 
           instance?.numero_whatsapp && 
           instance?.ia_config_id && 
           instance?.listener?.ativo;
  },
  
  // Obter status resumido
  getStatusSummary: (instance) => {
    if (!instance) return { status: 'not_found', message: 'Instância não encontrada' };
    
    if (whatsappUtils.isFullyOperational(instance)) {
      return { status: 'operational', message: 'Sistema funcionando completamente' };
    }
    
    const issues = [];
    if (instance.status !== 'conectada') issues.push('WhatsApp desconectado');
    if (!instance.numero_whatsapp) issues.push('Número não identificado');
    if (!instance.ia_config_id) issues.push('IA não configurada');
    if (!instance.listener?.ativo) issues.push('Listener inativo');
    
    return { 
      status: 'partial', 
      message: `Problemas: ${issues.join(', ')}`,
      issues 
    };
  },
  
  // Formatar número WhatsApp
  formatPhoneNumber: (number) => {
    if (!number) return 'N/A';
    return number.replace(/^55/, '+55 ');
  }
};

export const analyticsAPI = {
  getResumo: (clienteId, dias = 30) => 
    api.get(`/analytics/resumo/${clienteId}?dias=${dias}`),
  
  getPeriodo: (clienteId, params) => 
    api.get(`/analytics/periodo/${clienteId}`, { params }),
  
  getComparativo: (clienteId, instancias, dias = 30) => 
    api.get(`/analytics/comparativo/${clienteId}?instancias=${instancias.join(',')}&dias=${dias}`),
  
  calcularManual: (instanciaId, data) => 
    api.get(`/analytics/calcular/${instanciaId}${data ? `?data=${data}` : ''}`),

  getConversasSentimento: (clienteId, params) =>
    api.get(`/analytics/sentimento/conversas/${clienteId}`, { params }),

  getPalavrasChave: (clienteId, params) =>
    api.get(`/analytics/palavras-chave/${clienteId}`, { params }),

  getContatosAtivos: (clienteId, params) =>
    api.get(`/analytics/contatos-ativos/${clienteId}`, { params }),

  getHorariosPico: (clienteId, params) =>
    api.get(`/analytics/horarios-pico/${clienteId}`, { params }),

  getTempoRespostaInstancias: (clienteId, params) =>
    api.get(`/analytics/tempo-resposta-instancias/${clienteId}`, { params }),

  getInstagramInsights: (clienteId, params = {}) =>
    api.get(`/analytics/instagram/${clienteId}`, { params }),

  getFacebookInsights: (clienteId, params = {}) =>
    api.get(`/analytics/facebook/${clienteId}`, { params }),
};

export const instagramPostsAPI = {
  getAll: (clienteId, instanciaId) =>
    api.get('/instagram-posts', { params: { cliente_id: clienteId, instancia_id: instanciaId } }),
  getOne: (id) => api.get(`/instagram-posts/${id}`),
  getDetails: (id) => api.get(`/instagram-posts/${id}/details`),
  create: (data) => api.post('/instagram-posts', data),
  update: (id, data) => api.put(`/instagram-posts/${id}`, data),
  remove: (id) => api.delete(`/instagram-posts/${id}`),
  upload: (formData) => api.post('/instagram-posts/upload', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  publishNow: (id) => api.post(`/instagram-posts/${id}/publish-now`),
  importPosts: (instanciaId, clienteId) => api.post(`/instagram-posts/import/${instanciaId}`, { cliente_id: clienteId }),
  importSingle: (mediaId, platform, pageId) => api.post('/instagram-posts/import-single', { mediaId, platform, pageId }),
  // Comentários
  importComments: (id) => api.post(`/instagram-posts/${id}/comments/import`),
  importAllComments: (clienteId) => api.post(`/instagram-posts/comments/import-all`, { cliente_id: clienteId }),
  importFacebookComments: (clienteId) => api.post(`/instagram-posts/comments/import-facebook/${clienteId}`),
  getComments: (id) => api.get(`/instagram-posts/${id}/comments`),
  analyzePost: (id) => api.post(`/instagram-posts/${id}/analyze`),
  getTopCommenters: (clienteId, platform) => 
    api.get(`/instagram-posts/comments/top-commenters/${clienteId}${platform ? `?platform=${platform}` : ''}`),
  getTopCommentersConsolidated: (clienteId) => 
    api.get(`/instagram-posts/comments/top-commenters-consolidated/${clienteId}`),
  getUserComments: (clienteId, username, platform) => 
    api.get(`/instagram-posts/comments/user/${clienteId}/${username}${platform ? `?platform=${platform}` : ''}`),
};

export const cobrancaAPI = {
  getCotacao: () => api.get('/cobranca/cotacao'),
  getConfig: (clienteId) => api.get(`/cobranca/configuracoes/${clienteId}`),
  getAllConfigs: () => api.get('/cobranca/configuracoes'),
  saveConfig: (clienteId, data) => api.post(`/cobranca/configuracoes/${clienteId}`, data),
  deleteConfig: (id) => api.delete(`/cobranca/configuracoes/${id}`),
  calcular: (clienteId, totalContatos, tipo = 'marketing') =>
    api.get(`/cobranca/calcular?clienteId=${clienteId}&totalContatos=${totalContatos}&tipo=${tipo}`),
};

export const faturamentoAPI = {
  getResumo: (clienteId, mes) => api.get(`/faturamento/resumo?cliente_id=${clienteId}${mes ? `&mes=${mes}` : ''}`),
  getHistorico: (clienteId, meses = 6) => api.get(`/faturamento/historico?cliente_id=${clienteId}&meses=${meses}`),
  getFaturas: (clienteId) => api.get(`/faturamento/faturas?cliente_id=${clienteId}`),
  getCicloAtual: (clienteId) => api.get(`/faturamento/ciclo-atual?cliente_id=${clienteId}`),
  downloadExtratoPdf: (clienteId) => api.get(`/faturamento/extrato-pdf?cliente_id=${clienteId}`, { responseType: 'blob' }),
  emitirManual: (clienteId) => api.post(`/faturamento/emitir/${clienteId}`),
};

export const walletAPI = {
  getSaldo: (clienteId) => api.get(`/wallet/saldo/${clienteId}`),
  getExtrato: (clienteId, limit = 20, page = 1) => api.get(`/wallet/extrato/${clienteId}?limit=${limit}&page=${page}`),
  creditar: (data) => api.post('/wallet/creditar', data),
  debitar: (data) => api.post('/wallet/debitar', data),
  estorno: (data) => api.post('/wallet/estorno', data),
  verificar: (clienteId, valor) => api.get(`/wallet/verificar/${clienteId}?valor=${valor}`),
};

export const walletClienteAPI = {
  getSaldo: (clienteId) => api.get(`/wallet-cliente/saldo/${clienteId}`),
  getExtrato: (clienteId, limit = 20, page = 1) => api.get(`/wallet-cliente/extrato/${clienteId}?limit=${limit}&page=${page}`),
  criarRecarga: (data) => api.post('/wallet-cliente/recarga', data),
  listarCobrancas: (clienteId) => api.get(`/wallet-cliente/cobrancas/${clienteId}`),
  consultarCobranca: (id) => api.get(`/wallet-cliente/cobranca/${id}`),
  downloadPdf: (codigoSolicitacao) => api.get(`/inter-pix/cobranca/${codigoSolicitacao}/pdf`, { responseType: 'blob' }),
  upgrade: (planoId) => api.post('/planos/upgrade', { plano_id: planoId }),
};

export const concorrentesAPI = {
  list: () => api.get('/concorrentes'),
  create: (data) => api.post('/concorrentes', data),
  get: (id) => api.get(`/concorrentes/${id}`),
  delete: (id) => api.delete(`/concorrentes/${id}`),
  analisar: (id, accessToken, days) => api.post(`/concorrentes/${id}/analisar`, { access_token: accessToken, days }),
  historico: (id) => api.get(`/concorrentes/${id}/historico`),
  trends: (days) => api.get(`/concorrentes/trends${days ? `?days=${days}` : ''}`),
  gerarIdeias: (days, tipos) => api.post(`/concorrentes/gerar-ideias?${days ? `days=${days}` : ''}${tipos ? `&tipos=${tipos}` : ''}`),
  expandirIdeia: (ideia, tom) => api.post('/concorrentes/expandir-ideia', { ideia, tom }),
  gerarCalendario: (days, tipos) => api.post(`/concorrentes/gerar-calendario?${days ? `days=${days}` : ''}${tipos ? `&tipos=${tipos}` : ''}`),
  listRoteiros: () => api.get('/concorrentes/roteiros'),
  getRoteiro: (id) => api.get(`/concorrentes/roteiros/${id}`),
  updateRoteiro: (id, data) => api.put(`/concorrentes/roteiros/${id}`, data),
  deleteRoteiro: (id) => api.delete(`/concorrentes/roteiros/${id}`),
};

export const notificacoesAPI = {
  listar: (page = 1, limit = 20) => api.get(`/notificacoes?page=${page}&limit=${limit}`),
  contar: () => api.get('/notificacoes/count'),
  marcarLida: (id) => api.patch(`/notificacoes/${id}/lida`),
  marcarTodasLidas: () => api.patch('/notificacoes/marcar-todas-lidas'),
  marcarLidaPorContato: (telefone) => api.patch(`/notificacoes/contato/${encodeURIComponent(telefone)}/lida`),
};

export const commentsAPI = {
  reply: (commentId, postId, message) => api.post(`/instagram-posts/comments/${commentId}/reply`, { message, postId }),
};

export const cadastroAPI = {
  verificar: (clienteId) => api.get(`/clientes/cadastro-completo/${clienteId}`),
  completar: (clienteId, data) => api.patch(`/clientes/completar-cadastro/${clienteId}`, data),
  onboarding: (clienteId) => api.get(`/clientes/onboarding/${clienteId}`),
};

export const emailAPI = {
  resendConfirmation: () => api.post('/auth/resend-confirmation'),
};

export const integracoesAPI = {
  getByCliente: (clienteId) => api.get(`/integracoes/cliente/${clienteId}`),
  // Chassi genérico
  getCatalogo: () => api.get('/integracoes/catalogo'),
  getTodasDoCliente: (clienteId) => api.get(`/integracoes/cliente/${clienteId}/todas`),
  getCapabilities: (clienteId) => api.get(`/integracoes/cliente/${clienteId}/capabilities`),
  habilitar: (clienteId, provedor, habilitado) => api.put(`/integracoes/cliente/${clienteId}/${provedor}/habilitar`, { habilitado }),
  configurar: (id, credenciais) => api.post(`/integracoes/${id}/configurar`, credenciais),
  criar: (data) => api.post('/integracoes', data),
  sync: (id) => api.post(`/integracoes/${id}/sync`),
  testar: (id) => api.post(`/integracoes/${id}/testar`),
  deletarIntegracao: (id) => api.post(`/integracoes/${id}/deletar`),
  autorizarBling: (clienteId, nome) => api.post('/integracoes/bling/authorize', { cliente_id: clienteId, nome }),
  // Pedidos
  listar: () => api.get('/pedidos'),
  pedidos: (telefone) => api.get(`/pedidos/telefone/${telefone}`),
  atualizarStatusPedido: (pedidoId, status) => api.patch(`/pedidos/${pedidoId}/status`, { status }),
  cancelarPedido: (pedidoId) => api.post(`/pedidos/${pedidoId}/cancelar`),
  editarPedido: (pedidoId, dados) => api.patch(`/pedidos/${pedidoId}`, dados),
  // Produtos
  buscarProduto: (q) => api.get(`/produtos/busca?q=${encodeURIComponent(q)}`),
  listarProdutos: (params) => api.get('/produtos', { params }),
  categorias: () => api.get('/produtos/categorias'),
  statsProdutos: () => api.get('/produtos/stats'),
  atualizarCategoria: (id, categoria) => api.patch(`/produtos/${id}/categoria`, { categoria }),
};

export const coberturaAPI = {
  getCobertura: (clienteId, filtros = {}) => {
    const params = {};
    // Cidades e tags são múltiplas: lista separada por vírgula
    if (filtros.cidades?.length) params.cidades = filtros.cidades.join(',');
    else if (filtros.cidade) params.cidade = filtros.cidade;
    if (filtros.tags?.length) params.tags = filtros.tags.join(',');
    if (filtros.optin !== undefined) params.optin = String(filtros.optin);
    if (filtros.segmento_ids) params.segmento_ids = filtros.segmento_ids.join(',');
    return api.get(`/cobertura/${clienteId}`, { params });
  },
  getContatos: (clienteId, filtros = {}) => {
    const params = { page: filtros.page || 1, limit: filtros.limit || 50 };
    // Cidades e tags são múltiplas: enviadas como lista separada por vírgula
    if (filtros.cidades?.length) params.cidades = filtros.cidades.join(',');
    else if (filtros.cidade) params.cidade = filtros.cidade;
    if (filtros.tags?.length) params.tags = filtros.tags.join(',');
    else if (filtros.tag) params.tag = filtros.tag;
    if (filtros.optin !== undefined) params.optin = String(filtros.optin);
    if (filtros.em_grupo !== undefined) params.em_grupo = String(filtros.em_grupo);
    if (filtros.segmento_id) params.segmento_id = filtros.segmento_id;
    if (filtros.order_by) params.order_by = filtros.order_by;
    if (filtros.order_dir) params.order_dir = filtros.order_dir;
    if (filtros.cadastrado_apos) params.cadastrado_apos = filtros.cadastrado_apos;
    if (filtros.cadastrado_antes) params.cadastrado_antes = filtros.cadastrado_antes;
    if (filtros.pontos_minimos !== undefined) params.pontos_minimos = String(filtros.pontos_minimos);
    return api.get(`/cobertura/${clienteId}/contatos`, { params });
  },
  // Resumo de um segmento (página de detalhes)
  getSegmento: (clienteId, segmentoId, filtros = {}) => {
    const params = {};
    if (filtros.cidades?.length) params.cidades = filtros.cidades.join(',');
    if (filtros.tags?.length) params.tags = filtros.tags.join(',');
    if (filtros.optin !== undefined) params.optin = String(filtros.optin);
    if (filtros.em_grupo !== undefined) params.em_grupo = String(filtros.em_grupo);
    if (filtros.cadastrado_apos) params.cadastrado_apos = filtros.cadastrado_apos;
    return api.get(`/cobertura/${clienteId}/segmento/${segmentoId}`, { params });
  },
  // O backend responde text/csv; os filtros vão no corpo, sem aninhar em { filtros }
  exportar: (clienteId, filtros = {}) =>
    api.post(`/cobertura/${clienteId}/exportar`, filtros, { responseType: 'text' }),
  // Cria segmentação para campanha a partir do recorte atual
  criarSegmentacao: (clienteId, nome, filtros = {}) =>
    api.post(`/cobertura/${clienteId}/criar-segmentacao`, { nome, filtros }),
  listarSegmentos: (fonteId) => api.get(`/cobertura/fontes/${fonteId}/segmentos`),
  dispararSync: (fonteId, clienteId, segmentoIds) => api.post(`/cobertura/fontes/${fonteId}/sync`, { cliente_id: clienteId, segmento_ids: segmentoIds }),
  getStatusSync: (fonteId) => api.get(`/cobertura/fontes/${fonteId}/sync/status`),
};

export const vendedoresAPI = {
  list: () => api.get('/admin/vendedores'),
  get: (id) => api.get(`/admin/vendedores/${id}`),
  stats: (id, params) => api.get(`/admin/vendedores/${id}/stats`, { params }),
  create: (data) => api.post('/admin/vendedores', data),
  update: (id, data) => api.patch(`/admin/vendedores/${id}`, data),
  remove: (id) => api.delete(`/admin/vendedores/${id}`),
};

export const leadsAPI = {
  list: (params) => api.get('/admin/leads', { params }),
  resumo: (params) => api.get('/admin/leads/resumo', { params }),
};

export const leadContatoAPI = {
  list: (params) => api.get('/admin/leads-contato', { params }),
  get: (id) => api.get(`/admin/leads-contato/${id}`),
  resumo: (params) => api.get('/admin/leads-contato/resumo', { params }),
  update: (id, data) => api.patch(`/admin/leads-contato/${id}`, data),
};

export const metasAPI = {
  listarPlanos: (clienteId) => api.get('/metas/planos', { params: { cliente_id: clienteId } }),
  buscarPlano: (id) => api.get(`/metas/planos/${id}`),
  criarPlano: (data) => api.post('/metas/planos', data),
  atualizarPlano: (id, data) => api.put(`/metas/planos/${id}`, data),
  removerPlano: (id) => api.delete(`/metas/planos/${id}`),
  criarFase: (planoId, data) => api.post(`/metas/planos/${planoId}/fases`, data),
  atualizarFase: (id, data) => api.put(`/metas/fases/${id}`, data),
  removerFase: (id) => api.delete(`/metas/fases/${id}`),
  criarSegmento: (faseId, data) => api.post(`/metas/fases/${faseId}/segmentos`, data),
  atualizarSegmento: (id, data) => api.put(`/metas/segmentos/${id}`, data),
  removerSegmento: (id) => api.delete(`/metas/segmentos/${id}`),
  getProgresso: (planoId) => api.get(`/metas/planos/${planoId}/progresso`),
  getHistorico: (planoId, dias = 30) => api.get(`/metas/planos/${planoId}/historico`, { params: { dias } }),
  forcarSnapshot: (planoId) => api.post(`/metas/planos/${planoId}/snapshot`),
  backfill: (planoId, dias = 60) => api.post(`/metas/planos/${planoId}/backfill`, { dias }),
  definirBaseline: (faseId, data) => api.put(`/metas/fases/${faseId}/baseline`, data),
};

export default api;